import React from "react";
import { Group, Image, Text } from "react-konva";
import { FurnitureShape as IFurnitureShape } from "../../../../types";
import { mergeRefs } from "../../../../utils/mergeRef";
import { loadColoredSvgUrlImage } from "../../../../utils/svgAssetCache";
import { DEFAULT_FURNITURE_FILL_COLOR } from "../../defines";
import { getTextProps } from "../../helpers/getTextProps";
import { getWindowKonvaObject } from "../../helpers/konva/getWindowKonvaObject";
import { ShapeCommonProps } from "../../types";
import { useShape } from "../ShapeContext/useShape";
import { FurnitureShapeSVG } from "./FurnitureShapeSVG";

export interface FurnitureShapeComponentProps extends ShapeCommonProps {
  renderMode?: "konva" | "svg";
}

export const FurnitureShape = React.forwardRef<
  React.ComponentRef<typeof Group>,
  FurnitureShapeComponentProps
>(({ renderMode = "konva", ...commonProps }, ref) => {
  const { shape, image, setImage } = useShape();
  const furnitureShape = shape as IFurnitureShape;
  const shapeRef = React.useRef<React.ComponentRef<typeof Group>>(null);
  const fillColor = shape.color || DEFAULT_FURNITURE_FILL_COLOR;
  const imgUrl = furnitureShape?.furniture?.url;

  React.useEffect(() => {
    if (!imgUrl) return;
    let cancelled = false;

    // Shared session cache: one fetch per url and one rasterized image per
    // (url, color), no matter how many shapes reference the same asset.
    loadColoredSvgUrlImage(imgUrl, fillColor)
      .then((img) => {
        if (cancelled) return;
        setImage(img);
        getWindowKonvaObject().stages?.[0]?.fire?.("contentChange");
      })
      .catch((error) => console.error("Error loading SVG:", error));

    return () => {
      cancelled = true;
    };
  }, [imgUrl, fillColor, setImage]);

  React.useEffect(() => {
    if (shape.type === "furniture" && image) {
      const layer = shapeRef.current?.getLayer();
      if (layer) {
        layer.batchDraw();
      }
    }
  }, [shape.type, image]);

  const { x, y, onTransformEnd, ...rest } = commonProps;

  // If renderMode is SVG, return SVG components
  if (renderMode === "svg") {
    return <FurnitureShapeSVG />;
  }

  // Default Konva rendering
  return (
    <Group
      width={shape.width}
      height={shape.height}
      ref={mergeRefs([shapeRef, ref])}
      {...commonProps}
    >
      <Image
        image={image || undefined}
        width={shape.width}
        height={shape.height}
        opacity={shape.locked ? 0.5 : 1}
        imageSmoothingEnabled={true}
        // Perfect draw double-buffers every image draw — pure cost for
        // plain images (no fill+stroke+opacity combo), and maps render
        // hundreds of these.
        perfectDrawEnabled={false}
      />
      {furnitureShape.text && (
        <Text
          text={furnitureShape.text}
          y={furnitureShape.height + 10}
          x={0}
          ref={(node) => {
            if (node) {
              const textWidth = node.width();
              if (furnitureShape.textAlign === "center") {
                node.x((furnitureShape.width - textWidth) / 2);
              } else if (furnitureShape.textAlign === "right") {
                node.x(furnitureShape.width - textWidth);
              } else {
                node.x(0);
              }
            }
          }}
          {...getTextProps(furnitureShape)}
          wrap={"none"}
        />
      )}
    </Group>
  );
});
